/**
 * 처방 검수 — 이전 처방 의약품 불러오기 패널 (새 창 화면 본문).
 *
 * 같은 사용자·병의원의 최근 3개월(처방등록년월 기준) 확정 처방 의약품을 보여주고,
 * 원외·원내 처방량을 입력한 약만 그 수량으로 상세 화면의 미저장 행으로 넘긴다.
 * 저장은 상세 화면의 기존 [의약품 일괄 추가]가 한다.
 */
import type { PreviousPrescriptionDrugResponse } from '@/entities/prescription/api/prescription.api';
import { usePreviousPrescriptionDrugs } from '@/entities/prescription/hooks/usePrescriptions';
import { QuantityInput } from '@/pages/prescriptions/QuantityInput';
import {
  formatPrice,
  formatQuantity,
  formatYearMonth,
} from '@/pages/prescriptions/prescriptionUtils';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/shared/components/ui/combobox';
import { Label } from '@/shared/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ChevronDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

/** 처방량을 입력한 약과 그 수량 */
export interface PreviousDrugAddition {
  drug: PreviousPrescriptionDrugResponse;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
}

interface PreviousPrescriptionDrugsPanelProps {
  prescriptionImageCode: string;
  /** 상세 화면 미저장 행의 의약품 코드(trim) — 서버는 저장된 행만 알기 때문에 화면에서 막는다 */
  stagedDrugCodes: ReadonlySet<string>;
  /** 상세 화면에 저장된 의약품 코드(trim) — 조회 이후 저장된 약도 막는다 */
  registeredDrugCodes?: ReadonlySet<string>;
  /** 고른 약을 상세 화면으로 보낸다. 보냈으면 true(입력한 처방량을 비운다) */
  onAdd: (additions: PreviousDrugAddition[]) => boolean;
  onClose: () => void;
  /** 추가를 막는 사유(상세 화면 연결 끊김·쓰기 권한 없음 등). 있으면 [추가] 비활성 */
  addDisabledReason?: string | null;
}

const EMPTY_CODES: ReadonlySet<string> = new Set();

/**
 * 고를 수 없는 사유. null 이면 선택 가능.
 * 서버 `/drugs` 는 중복 행·마스터 없는 약(약가 0)을 막지 않으므로 여기서 막는다.
 */
export const getPreviousDrugDisabledReason = (
  drug: PreviousPrescriptionDrugResponse,
  stagedDrugCodes: ReadonlySet<string>,
  registeredDrugCodes: ReadonlySet<string> = EMPTY_CODES,
): string | null => {
  if (
    drug.alreadyInCurrentPrescription ||
    registeredDrugCodes.has(drug.drugCode.trim())
  ) {
    return '이미 등록됨';
  }
  if (stagedDrugCodes.has(drug.drugCode.trim())) return '미저장 행에 있음';
  if (!drug.isDrugInfoExists) return '의약품 정보 없음';
  return null;
};

const ALL_PHARMA_COMPANIES = '전체';

/**
 * 제약사 필터용 이름 — '(주)유유제약'과 '유유제약'처럼 법인 표기만 다른 이름을 같은 제약사로 묶는다.
 * 의약품 마스터의 제약사명 표기가 섞여 있어서 화면에서 맞춘다.
 */
export const normalizePharmaCompanyName = (name?: string | null): string =>
  (name ?? '').replace(/\(주\)|㈜|주식회사/g, '').replace(/\s+/g, '');

/**
 * 불러온 내역에 있는 제약사만 드롭다운에 보여준다. 법인 표기를 뺀 이름으로 묶는다.
 * (제약사 없는 약은 '전체'에서만 보인다)
 */
export const getPharmaCompanyOptions = (
  drugs: PreviousPrescriptionDrugResponse[],
): string[] =>
  [
    ...new Set(
      drugs
        .map((drug) => normalizePharmaCompanyName(drug.pharmaCompanyName))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b, 'ko'));

type QuantityDraft = {
  outHospitalQuantity: number;
  inHospitalQuantity: number;
};

const EMPTY_QUANTITY: QuantityDraft = {
  outHospitalQuantity: 0,
  inHospitalQuantity: 0,
};

/**
 * 전월(마지막 처방월) 총 처방량 — 원외+원내.
 * 서버 배포 전이거나 둘 다 비어 있으면 null 이라 '-'로 보여준다.
 */
export const getLastMonthTotalQuantity = (
  drug: PreviousPrescriptionDrugResponse,
): number | null => {
  const out = drug.lastMonthOutHospitalQuantity;
  const inHospital = drug.lastMonthInHospitalQuantity;
  if (out == null && inHospital == null) return null;
  return (out ?? 0) + (inHospital ?? 0);
};

/** 원외·원내 중 하나라도 입력했으면 추가 대상이다 */
const hasQuantity = (quantity: QuantityDraft) =>
  quantity.outHospitalQuantity !== 0 || quantity.inHospitalQuantity !== 0;

export const PreviousPrescriptionDrugsPanel: React.FC<
  PreviousPrescriptionDrugsPanelProps
> = ({
  prescriptionImageCode,
  stagedDrugCodes,
  registeredDrugCodes = EMPTY_CODES,
  onAdd,
  onClose,
  addDisabledReason,
}) => {
  const { data, isFetching, isError } = usePreviousPrescriptionDrugs(
    prescriptionImageCode,
    true,
  );
  const drugs = data ?? [];
  const [quantities, setQuantities] = useState<Record<string, QuantityDraft>>(
    {},
  );
  /** null = 전체 */
  const [pharmaCompany, setPharmaCompany] = useState<string | null>(null);
  const [pharmaQuery, setPharmaQuery] = useState('');
  const [pharmaListOpen, setPharmaListOpen] = useState(false);

  // Combobox는 items 참조가 바뀌면 하이라이트를 맨 위로 되돌린다.
  // 방향키로 제약사를 바로 바꿀 때 하이라이트가 튀지 않도록 목록 참조를 고정한다
  const pharmaCompanyOptions = useMemo(
    () => getPharmaCompanyOptions(data ?? []),
    [data],
  );
  const allPharmaComboboxItems = useMemo(
    () => [ALL_PHARMA_COMPANIES, ...pharmaCompanyOptions],
    [pharmaCompanyOptions],
  );
  // 드롭다운을 열면 전체 제약사를 보여주고, 글자를 입력하면 그 글자로 후보를 좁힌다.
  // 검색 중에는 '전체'를 빼서 Enter 로 첫 번째 일치 제약사가 바로 골라지게 한다
  const query = pharmaQuery.trim();
  const isSearching = query !== '' && query !== pharmaCompany;
  const searchedPharmaComboboxItems = useMemo(
    () =>
      pharmaCompanyOptions.filter((name) =>
        name.includes(normalizePharmaCompanyName(query)),
      ),
    [pharmaCompanyOptions, query],
  );
  const pharmaComboboxItems = isSearching
    ? searchedPharmaComboboxItems
    : allPharmaComboboxItems;

  const selectPharmaCompany = (name: string | null) => {
    const next = name == null || name === ALL_PHARMA_COMPANIES ? null : name;
    setPharmaCompany(next);
    setPharmaQuery(next ?? '');
  };

  // 제약사를 바꿔도 입력한 수량은 유지한다 — 여러 제약사 약을 섞어 추가할 수 있게
  const visibleDrugs =
    pharmaCompany == null
      ? drugs
      : drugs.filter(
          (drug) =>
            normalizePharmaCompanyName(drug.pharmaCompanyName) ===
            pharmaCompany,
        );

  const changeQuantity = (
    code: string,
    field: keyof QuantityDraft,
    value: number,
  ) => {
    setQuantities((prev) => ({
      ...prev,
      [code]: { ...(prev[code] ?? EMPTY_QUANTITY), [field]: value },
    }));
  };

  // 처방량을 입력한 약만 추가한다. 고를 수 없는 약은 입력란이 없어 여기 들어오지 않는다
  const additions: PreviousDrugAddition[] = drugs
    .filter(
      (drug) =>
        getPreviousDrugDisabledReason(
          drug,
          stagedDrugCodes,
          registeredDrugCodes,
        ) == null && hasQuantity(quantities[drug.drugCode] ?? EMPTY_QUANTITY),
    )
    .map((drug) => ({ drug, ...quantities[drug.drugCode] }));

  // 새 창은 열어 둔 채 이어서 고를 수 있게, 보낸 뒤에는 입력한 처방량만 비운다
  const handleAdd = () => {
    if (onAdd(additions)) setQuantities({});
  };

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold">이전 처방 불러오기</h1>
        <p className="text-sm text-muted-foreground">
          같은 사용자·병의원의 최근 3개월(처방등록년월 기준) 확정 처방
          의약품입니다. 원외·원내 처방량을 입력한 의약품만 상세 화면에 미저장
          행으로 추가되며, 상세 화면에서 [의약품 일괄 추가]를 눌러야 저장됩니다.
        </p>
      </header>

      {isFetching ? (
        <div className="py-12">
          <LoadingSpinner tip="이전 처방 의약품을 불러오는 중..." />
        </div>
      ) : isError ? (
        <p className="py-8 text-center text-sm text-destructive">
          이전 처방 의약품을 불러오지 못했습니다.
        </p>
      ) : drugs.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          최근 3개월 동안 확정 처방된 의약품이 없습니다.
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="previous-drugs-pharma-company" className="shrink-0">
              제약사
            </Label>
            <div className="relative w-64 min-w-0">
              <Combobox
                items={pharmaComboboxItems}
                itemToStringValue={(name) => name}
                value={pharmaCompany ?? ALL_PHARMA_COMPANIES}
                onValueChange={selectPharmaCompany}
                // 드롭다운처럼 위아래 키로 옮기면 바로 그 제약사로 거른다. 검색 중에는 Enter로 고른다
                onHighlightChange={(name) => {
                  if (!isSearching) selectPharmaCompany(name);
                }}
                open={pharmaListOpen}
                onOpenChange={setPharmaListOpen}
              >
                <ComboboxInput
                  id="previous-drugs-pharma-company"
                  aria-label="제약사"
                  placeholder="전체"
                  clearable={false}
                  inputClassName="pr-9"
                  value={pharmaQuery}
                  onValueChange={(next) => {
                    setPharmaQuery(next);
                    // 입력을 다 지우면 전체로 돌아간다
                    if (next.trim() === '') setPharmaCompany(null);
                  }}
                  // 드롭다운처럼 클릭하면 목록을 펼치고, 바로 덮어 써서 검색할 수 있게 전체 선택한다
                  onClick={() => setPharmaListOpen(true)}
                  onFocus={(event) => event.currentTarget.select()}
                  // 고르지 않고 벗어나면 입력을 현재 필터 이름으로 되돌린다
                  onBlur={() => setPharmaQuery(pharmaCompany ?? '')}
                />
                <ComboboxContent>
                  <ComboboxEmpty>일치하는 제약사가 없습니다.</ComboboxEmpty>
                  <ComboboxList<string>>
                    {(name) => (
                      <ComboboxItem key={name} value={name}>
                        {name}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
              <ChevronDown
                aria-hidden
                className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
            </div>
            <span className="shrink-0 text-sm text-muted-foreground">
              {visibleDrugs.length}건
            </span>
          </div>
          {/* 열이 10개라 셀 여백을 줄이고 수량 머리글은 두 줄로 접어 한 화면에 들어오게 한다 */}
          <Table
            className="min-w-0 [&_td]:px-2 [&_th]:px-2"
            containerClassName="min-h-0 flex-1 overflow-y-auto rounded border"
          >
            <TableHeader className="sticky top-0 z-10 bg-white">
              <TableRow>
                <TableHead>의약품 코드</TableHead>
                <TableHead>의약품명</TableHead>
                <TableHead>제약사</TableHead>
                <TableHead className="text-right">약가</TableHead>
                {/* 전월 = 서버의 마지막 처방월(lastPrescriptionRegisteredYearMonth). 입력 참고용 */}
                <TableHead className="min-w-[76px] whitespace-normal text-right">
                  전월 원외 처방량
                </TableHead>
                <TableHead className="min-w-[76px] whitespace-normal text-right">
                  전월 원내 처방량
                </TableHead>
                <TableHead className="min-w-[76px] whitespace-normal text-right">
                  전월 총 처방량
                </TableHead>
                <TableHead className="w-[92px] whitespace-normal">
                  원외 처방량
                </TableHead>
                <TableHead className="w-[92px] whitespace-normal">
                  원내 처방량
                </TableHead>
                <TableHead className="min-w-[76px] whitespace-normal text-right">
                  총 처방량
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleDrugs.map((drug) => {
                const disabledReason = getPreviousDrugDisabledReason(
                  drug,
                  stagedDrugCodes,
                  registeredDrugCodes,
                );
                const quantity = quantities[drug.drugCode] ?? EMPTY_QUANTITY;
                return (
                  <TableRow
                    key={drug.drugCode}
                    className={disabledReason ? 'opacity-60' : undefined}
                  >
                    <TableCell>{drug.drugCode}</TableCell>
                    <TableCell className="min-w-[180px] whitespace-normal">
                      {drug.drugName ?? '-'}
                      {disabledReason ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({disabledReason})
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="min-w-[88px] whitespace-normal">
                      {drug.pharmaCompanyName ?? '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPrice(drug.price)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatQuantity(drug.lastMonthOutHospitalQuantity)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatQuantity(drug.lastMonthInHospitalQuantity)}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      aria-label={`${drug.drugCode} 전월 총 처방량`}
                    >
                      {formatQuantity(getLastMonthTotalQuantity(drug))}
                      {/* 마지막 처방월이 바로 전달이 아닐 수 있어 어느 달 값인지 함께 보여준다 */}
                      <div className="text-xs text-muted-foreground">
                        {formatYearMonth(
                          drug.lastPrescriptionRegisteredYearMonth,
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {disabledReason ? null : (
                        <QuantityInput
                          aria-label={`${drug.drugCode} 원외 처방량`}
                          className="h-8 w-20"
                          value={quantity.outHospitalQuantity}
                          onChange={(value) =>
                            changeQuantity(
                              drug.drugCode,
                              'outHospitalQuantity',
                              value,
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {disabledReason ? null : (
                        <QuantityInput
                          aria-label={`${drug.drugCode} 원내 처방량`}
                          className="h-8 w-20"
                          value={quantity.inHospitalQuantity}
                          onChange={(value) =>
                            changeQuantity(
                              drug.drugCode,
                              'inHospitalQuantity',
                              value,
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      aria-label={`${drug.drugCode} 총 처방량`}
                    >
                      {hasQuantity(quantity)
                        ? formatQuantity(
                            quantity.outHospitalQuantity +
                              quantity.inHospitalQuantity,
                          )
                        : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <footer className="flex items-center justify-end gap-2">
        {addDisabledReason ? (
          <span className="mr-auto text-sm text-destructive">
            {addDisabledReason}
          </span>
        ) : null}
        <Button variant="outline" onClick={onClose}>
          닫기
        </Button>
        <Button
          className="ruda-button"
          onClick={handleAdd}
          disabled={additions.length === 0 || Boolean(addDisabledReason)}
        >
          {additions.length > 0 ? `${additions.length}건 추가` : '추가'}
        </Button>
      </footer>
    </div>
  );
};
